import { useCallback, useRef } from "react";

/**
 * Reusable abort controller for cancelling in-flight async work (e.g. fetch).
 * Use with progress drawers: pass signal to the request and call cancel(onCloseDrawer)
 * when the user dismisses the drawer.
 */
export function useAbortController() {
  const ref = useRef<AbortController | null>(null);

  const createController = useCallback(() => {
    const controller = new AbortController();
    ref.current = controller;
    return controller;
  }, []);

  const cancel = useCallback((onCancel?: () => void) => {
    ref.current?.abort();
    ref.current = null;
    onCancel?.();
  }, []);

  const isAborted = useCallback((controller: AbortController) => {
    return controller.signal.aborted;
  }, []);

  const clearRef = useCallback(() => {
    ref.current = null;
  }, []);

  return { ref, createController, cancel, isAborted, clearRef };
}
